import Vue from 'vue'

export default Vue.extend({
    data() {
        return {
            form: false,
            data: this.$parent.$data,
            id: 'g-form-input',
            focus: false,
            errorMessageVisible: false,
            errorTooltipEl: null,
        }
    },
    created() {
        /**
         * This method is used to find the parent form of the input.
         * It will look for the form in the parent, grandparent and great-grandparent.
         * If it finds the form, it will set the form property to true and the data property to the form data.
         *
         * Todo: add support for setting a form reference directly on the input component, this will allow for more flexibility and less coupling.
         * See: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#form
         */
        let parent = this.$parent;

        for (let i = 0; i < 3; i++) {
            if (
                !!parent.$props &&
                !!parent.$props.hasOwnProperty('form') &&
                !!parent.$props.form.hasOwnProperty(this.name) &&
                parent.$vnode.tag.indexOf('g-form') !== -1
            ) {
                this.form = true
                this.data = parent.$props.form

                break
            }

            if(!parent.$parent) break

            parent = parent.$parent
        }

        // If this input is used without g-form, attach it to the first parent containing its property
        if(this.form === false) {
            parent = this.$parent

            for (let i = 0; i < 3; i++) {
                if (
                    !!parent.$data &&
                    !!parent.$data.hasOwnProperty(this.name)
                ) {
                    this.data = parent.$data
                    break
                }

                if(!parent.$parent) break

                parent = parent.$parent
            }
        }

        this.id = 'g-form-input-' + (Math.floor(Math.random() * 1000000))
    },
    inject: {
        formLocale: {
            default: () => () => 'en' // Default fallback
        },
        isTranslatable: {
            default: () => () => false
        }
    },
    props: {
        name: {
            type: String,
            required: true,
        },
        icon: {
            type: String,
            required: false,
        },
        label: {
            type: [String, Boolean],
            default: () => false,
        },
        type: {
            type: String,
            default: 'text',
        },
        size: {
            type: String,
            default: 'is-normal',
        },
        gClass: {
            type: String,
            default: '',
        },
        accept: {
            type: String,
            default: ''
        },
        animation: {
            type: [Boolean, String],
            default: true
        },
        placeholder: {
            type: String,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        translatable: {
            type: Boolean,
            default: false
        },
        autocomplete: {
            type: String,
            default: null
        }

    },
    methods: {
        emit(e) {
            this.$emit('input', e)
        },
        onInputFocus(e) {
            this.focus = true
            this.showErrorMessage()
            this.$emit('focus', e)
        },
        onInputBlur(e) {
            this.focus = false
            this.hideErrorMessage()
            this.$emit('blur', e)
        },

        showErrorMessage(event) {
            this.errorMessageVisible = true
            this.showErrorTooltip(event)
        },
        hideErrorMessage() {
            this.errorMessageVisible = false
            this.hideErrorTooltip()
        },
        showErrorTooltip(event) {
            if (!this.errorMessage) {
                return
            }

            // Create tooltip element if it doesn't exist
            if (!this.errorTooltipEl) {
                this.errorTooltipEl = document.createElement('div')
                this.errorTooltipEl.className = 'g-form-error-tooltip'
                document.body.appendChild(this.errorTooltipEl)
            }

            // Update content and reset styles for measurement
            this.errorTooltipEl.textContent = this.errorMessage
            this.errorTooltipEl.style.left = ''
            this.errorTooltipEl.style.right = ''
            this.errorTooltipEl.style.removeProperty('--arrow-right')
            this.errorTooltipEl.classList.remove('is-wrapped')

            // Position the tooltip
            const hoverArea = event?.target?.closest('.hover-area')
            if (hoverArea) {
                const rect = hoverArea.getBoundingClientRect()
                const windowWidth = window.innerWidth
                const windowPadding = 10 // Minimum distance from window edge
                const arrowOffset = 25 // Distance from right edge of tooltip to arrow center
                const arrowWidth = 10

                // Position vertically above the hover area
                this.errorTooltipEl.style.top = `${rect.top}px`

                // First, try single-line display
                // Temporarily make visible but off-screen to measure
                this.errorTooltipEl.style.visibility = 'hidden'
                this.errorTooltipEl.style.opacity = '0'
                this.errorTooltipEl.classList.add('is-visible')

                // Get the natural single-line width
                const tooltipWidth = this.errorTooltipEl.offsetWidth

                // Calculate the arrow's absolute position (center of hover area icon)
                const arrowTargetX = rect.right - (rect.width / 2) + 7

                // Calculate ideal tooltip position (arrow at 25px from right edge)
                // tooltipRight = arrowTargetX + arrowOffset + (arrowWidth / 2)
                const idealTooltipRight = arrowTargetX + arrowOffset
                const idealTooltipLeft = idealTooltipRight - tooltipWidth

                // Check if it fits on screen with single line
                if (idealTooltipLeft >= windowPadding && idealTooltipRight <= windowWidth - windowPadding) {
                    // Fits perfectly - position with right alignment to keep arrow pointing at icon
                    this.errorTooltipEl.style.right = `${windowWidth - idealTooltipRight}px`
                } else if (idealTooltipLeft < windowPadding) {
                    // Would overflow on the left - shift tooltip right
                    const shiftedLeft = windowPadding
                    const shiftedRight = shiftedLeft + tooltipWidth

                    if (shiftedRight <= windowWidth - windowPadding) {
                        // Shifted position fits - use left positioning and adjust arrow
                        this.errorTooltipEl.style.left = `${shiftedLeft}px`
                        // Calculate new arrow position relative to tooltip
                        const newArrowRight = shiftedRight - arrowTargetX - (arrowWidth / 2)
                        this.errorTooltipEl.style.setProperty('--arrow-right', `${newArrowRight}px`)
                    } else {
                        // Still doesn't fit - need to wrap
                        this.errorTooltipEl.classList.add('is-wrapped')
                        this.errorTooltipEl.style.left = `${windowPadding}px`
                        this.errorTooltipEl.style.right = `${windowPadding}px`
                        // Recalculate arrow position after wrapping
                        const wrappedRight = windowWidth - windowPadding
                        const newArrowRight = wrappedRight - arrowTargetX - (arrowWidth / 2)
                        this.errorTooltipEl.style.setProperty('--arrow-right', `${Math.max(10, newArrowRight)}px`)
                    }
                } else {
                    // Would overflow on the right - shift tooltip left
                    const shiftedRight = windowWidth - windowPadding
                    const shiftedLeft = shiftedRight - tooltipWidth

                    if (shiftedLeft >= windowPadding) {
                        // Shifted position fits
                        this.errorTooltipEl.style.right = `${windowPadding}px`
                        // Calculate new arrow position relative to tooltip
                        const newArrowRight = shiftedRight - arrowTargetX - (arrowWidth / 2)
                        this.errorTooltipEl.style.setProperty('--arrow-right', `${newArrowRight}px`)
                    } else {
                        // Still doesn't fit - need to wrap
                        this.errorTooltipEl.classList.add('is-wrapped')
                        this.errorTooltipEl.style.left = `${windowPadding}px`
                        this.errorTooltipEl.style.right = `${windowPadding}px`
                        // Recalculate arrow position after wrapping
                        const wrappedRight = windowWidth - windowPadding
                        const newArrowRight = wrappedRight - arrowTargetX - (arrowWidth / 2)
                        this.errorTooltipEl.style.setProperty('--arrow-right', `${Math.max(10, newArrowRight)}px`)
                    }
                }

                // Restore visibility
                this.errorTooltipEl.style.visibility = ''
                this.errorTooltipEl.style.opacity = ''
            }

            // Show the tooltip
            this.errorTooltipEl.classList.add('is-visible')
        },
        hideErrorTooltip() {
            if (this.errorTooltipEl) {
                this.errorTooltipEl.classList.remove('is-visible')
            }
        },
        destroyErrorTooltip() {
            if (this.errorTooltipEl) {
                this.errorTooltipEl.remove()
                this.errorTooltipEl = null
            }
        },

        focusOnInput() {
            this.$el.querySelector(`#${this.id}`).focus()
        }
    },
    computed: {
        errorMessage() {
            if (this.form && this.data.errors.has(this.name)) {
                return this.data.errors.get(this.name)
            }
        },
        labelText() {
            if (!!this.label) {
                return this.label
            }
        },
        errorType() {
            return this.hasError ? 'is-danger' : ''
        },
        hasError() {
            return this.form && this.data.errors.has(this.name)
        },

        isInputActive() {
            // If the input type is number and the value is 0, we consider it filled
            if (this.type === 'number' && this.inputValue === 0) {
                return true
            }

            return !!this.placeholder || this.focus || !!this.data[this.name]
        },
        labelAnimation() {
            return (typeof this.animation === 'boolean' ? this.animation : this.animation.toLowerCase() === 'true') && !!this.label
        },

        currentLocale() {
            return this.formLocale()
        },
        currentLocaleName() {
            if (!this.currentLocale) return ''
            const locale = this.$store.getters['application/availableLocalesTranslated']
                .find(l => l.value === this.currentLocale)
            return locale ? locale.name : this.currentLocale
        },
        localizedKey() {
            return this.isTranslatable(this.name)
                ? `${this.name}.${this.currentLocale}`
                : this.name;
        },
        inputValue: {
            get() {
                return this.data[this.localizedKey];
            },
            set(value) {
                this.$set(this.data, this.localizedKey, value);
            }
        },
        isFieldTranslatable() {
            return this.translatable && this.isTranslatable(this.name)
        }
    },
    beforeDestroy() {
        this.destroyErrorTooltip()
    },
})
